import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import classNames from 'classnames';
import TimeclockJobPositionItem from '../TimeclockJobPositionItem/TimeclockJobPositionItem';
import UnitEventModal from '../UnitEventModal/UnitEventModal';
import { clockInEmployeePosition, clockOutEmployeePosition } from 'modules/timeclock/state/actions';
import { ITimeClockEmployeeJobPosition } from 'common/interfaces/employee';
import { getPreparedTimeclockPositionsData } from 'modules/timeclock/utils/utils';
import { EmployeePayTypes } from 'common/constants/employee';

const useStyles = makeStyles((theme: Theme) => ({
  items: {
    '& .job-position-item:not(:last-of-type)': {
      borderBottom: `2px solid ${theme.palette.secondary.light}`,
    },
  },
  itemWrapper: {
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  serviceItemLabel: {
    marginBottom: theme.spacing(2),
  },
}));

interface ITimeClockJobPositionList {
  jobPositions: ITimeClockEmployeeJobPosition[];
  employeeId: number;
  isProfile?: boolean;
}

const TimeClockJobPositionList: FC<ITimeClockJobPositionList> = ({
  jobPositions,
  employeeId,
  isProfile = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [selectedUnitItem, setSelectedUnitItem] = useState<ITimeClockEmployeeJobPosition | null>(
    null,
  );

  const preparedData = useMemo(() => {
    if (jobPositions) return getPreparedTimeclockPositionsData(jobPositions);
    return [];
  }, [jobPositions]);

  const handleClockInOrOut = useCallback(
    (position: ITimeClockEmployeeJobPosition) => {
      if (position.payType === EmployeePayTypes.UNIT && !position.clockEdIn) {
        setSelectedUnitItem(position);
        return;
      }
      if (!position.clockEdIn) {
        dispatch(clockInEmployeePosition(employeeId, { jobPositionId: position.id }));
      } else {
        dispatch(clockOutEmployeePosition(employeeId, { jobPositionId: position.id }));
      }
    },
    [dispatch, employeeId],
  );

  return (
    <>
      <div className={classes.items}>
        {preparedData?.map(club => (
          <Box key={club?.id} className={classNames(classes.itemWrapper, 'job-position-item')}>
            <Typography variant="h5" color="textSecondary" className={classes.serviceItemLabel}>
              {club.clubName}
            </Typography>

            {club.items.map(position => (
              <Box key={position?.id} mb={2}>
                <TimeclockJobPositionItem onClockInOrOut={handleClockInOrOut} position={position} />
              </Box>
            ))}
          </Box>
        ))}
      </div>

      {Boolean(selectedUnitItem) && (
        <UnitEventModal
          isOpen={Boolean(selectedUnitItem)}
          selectedPosition={selectedUnitItem}
          onClose={() => setSelectedUnitItem(null)}
          employeeId={employeeId}
          isProfile={isProfile}
        />
      )}
    </>
  );
};

export default TimeClockJobPositionList;
